import React from 'react';

import styled from '@emotion/styled';
import { colors } from '@lightspeed/cirrus-tokens';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Box } from '@lightspeed/flame/Core';
import { IconClose } from '@lightspeed/flame/Icon/Close';
import { IconPrinters } from '@lightspeed/flame/Icon/Printers';
import { IconVerified } from '@lightspeed/flame/Icon/Verified';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Heading3, Text, TextLink } from '@lightspeed/flame/Text';

import PaymentProvider from 'app/src/payments/PaymentProvider';
import { isTerminalTypeDeprecated } from 'root/apps/shared_modules/Terminals';
import Modules from 'shared_modules/Modules';

import { TransactionState } from './commonData';
import { STRINGS } from './TerminalPaymentModal';

const IconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreWrapText = styled(Text)`
  white-space: pre-wrap;
`;

const StyledHeading3 = styled(Heading3)`
  &&& {
    text-transform: none;
    font-size: 18px;
    letter-spacing: normal;
  }
`;

/**
 * Prints the receipt for the last failed/cancelled transaction.
 */
const printReceipt = (salePaymentId: string) => {
  window.merchantos.print.printUrl({
    url: `/API/Account/${window.merchantos.session.getSystemCustomerID()}/DisplayTemplate/SalePayment/${salePaymentId}.html?template=SalePayment&print=1&page_width=auto&page_height=2000mm`,
    pageType: 'receipt',
  });
};

export const ModalBodyContent = ({
  transactionState,
  selectedTerminal,
  transactionMessage,
}: {
  transactionState: string;
  selectedTerminal?: any;
  transactionMessage?: string | null;
}) => {
  let image = null;
  let title = null;
  let message = null;
  switch (transactionState) {
    case TransactionState.COMPLETE:
      image = <IconVerified size="96px" color="green-700" />;
      title = 'Payment Approved';
      break;
    case TransactionState.CONNECTION_DEGRADED:
      image = (
        <>
          <Spinner color="blue-300" baseColor2="gray-200" size="96px" />
          <IconWarning baseColor1="orange" detailsColor1="gray-800" size="32px" />
        </>
      );
      title = STRINGS.CONNECTIVITY[transactionState].title;
      message = STRINGS.CONNECTIVITY[transactionState].message;
      break;
    case TransactionState.CONNECTION_KO:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = STRINGS.CONNECTIVITY[transactionState].title;
      message = STRINGS.CONNECTIVITY[transactionState].message;
      break;
    case TransactionState.CANCELED:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = 'Payment cancelled';
      message = 'The payment was cancelled on the terminal.';
      break;
    case TransactionState.CANCELLED_FROM_POS:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = 'Payment cancelled';
      message =
        transactionMessage && transactionMessage !== ''
          ? transactionMessage
          : 'You successfully cancelled the payment.';
      break;
    case TransactionState.CANCEL_UNAVAILABLE:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = 'This transaction cannot be cancelled right now';
      message = 'Give it a few seconds and try again.';
      break;
    case TransactionState.NON_CANCELLABLE_STATE:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = 'The transaction can not be cancelled';
      message = 'It is in a non-cancellable state.';
      break;
    case TransactionState.DECLINED:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = 'Payment declined';
      message = transactionMessage;
      break;

    case TransactionState.FAILED:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = 'Payment failed';
      break;

    case TransactionState.ERROR:
    case TransactionState.TERMINAL_ERROR:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = 'Payment error';
      message = transactionMessage;
      break;

    case TransactionState.CANCEL_FROM_TERMINAL:
      image = (
        <img src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/payments/cancelFromGeniusTerminal@2x.png" title="terminal" alt="" />
      );
      break;

    case TransactionState.CANCEL_FROM_POS_UNREFERENCED_REFUND:
      image = <Spinner color="blue-300" baseColor2="gray-200" size="96px" />;
      title = 'Complete refund on terminal';
      message = 'Ask the client to follow the instructions on the terminal';
      break;

    case TransactionState.SERVICE_EXCEPTION:
      image = <IconClose color={colors.colorsMap.maple} size="96px" />;
      title = 'Please contact support';
      message = transactionMessage;
      break;

    default:
      break;
  }

  const isError = [TransactionState.ERROR, TransactionState.TERMINAL_ERROR].includes(
    transactionState
  );

  return (
    <div className="quick-pay-modal__body cirrus">
      {Modules.hasModule('payments.deprecated_terminal') &&
        isTerminalTypeDeprecated(selectedTerminal?.terminalModel?.attributes?.type) &&
        isError && (
          <Alert data-test="deprecated-terminal-error-alert" type="danger" noCloseBtn mb={2}>
            This terminal is no longer supported and may cause issues taking payments.{' '}
            <TextLink
              href="https://retail-support.lightspeedhq.com/hc/en-us/articles/360038226154-Setting-up-the-Verifone-P400-with-Lightspeed-Payments"
              target="_blank"
            >
              Learn more
            </TextLink>{' '}
            about upgrading to faster, more reliable terminal.
          </Alert>
        )}
      <IconWrapper>{image}</IconWrapper>
      <div className="quick-pay-modal__body__message">
        {title && <StyledHeading3 mt="2">{title}</StyledHeading3>}
        {message && (
          <PreWrapText mt="2" size="small" color="gray-600">
            {message}
          </PreWrapText>
        )}
      </div>
    </div>
  );
};

export const ModalExtraBodyContent = ({
  transactionState,
  paymentProvider,
  salePaymentId,
}: {
  transactionState: string;
  paymentProvider: PaymentProvider;
  salePaymentId?: string;
}) => {
  switch (transactionState) {
    case TransactionState.CANCELED:
    case TransactionState.DECLINED:
      if (paymentProvider.isPlatformV3() || salePaymentId === undefined) return null;
      return (
        <div className="quick-pay-modal__body__footer quick-pay-modal__body__footer--centered">
          <Button onClick={() => printReceipt(salePaymentId)}>
            <IconPrinters color="gray-200" />
            <span>Print Receipt</span>
          </Button>
        </div>
      );
    case TransactionState.CANCEL_FROM_TERMINAL:
      return (
        <div
          data-test="cancel-from-terminal"
          className="quick-pay-modal__body__footer quick-pay-modal__body__footer--centered"
        >
          Payment must be cancelled on the terminal.
        </div>
      );
    default:
      return null;
  }
};

export const ModalFooterContent = ({
  transactionState,
  showRetryButton,
  isCancelDisabled,
  handleCloseModal,
  handleBackInProgress,
  handleRetryPaymentClicked,
  handleCancelWhileInProgress,
  syncingWithServer,
  handleTerminalSelectionOption,
  multiTerminal,
}: {
  transactionState: string;
  showRetryButton?: boolean;
  isCancelDisabled: boolean;
  handleCloseModal?: () => void;
  handleBackInProgress?: () => void;
  handleRetryPaymentClicked?: () => void;
  handleCancelWhileInProgress: () => void;
  syncingWithServer?: boolean;
  handleTerminalSelectionOption?: () => void;
  multiTerminal?: boolean;
}) => {
  switch (transactionState) {
    case TransactionState.COMPLETE:
      return <div className="quick-pay-modal__footer quick-pay-modal__footer" />;
    case TransactionState.IN_PROGRESS:
      return (
        <div className="quick-pay-modal__footer quick-pay-modal__footer">
          {/* Hide the Terminal Selection button if there is only one terminal */}
          {multiTerminal && (
            <Button
              disabled={isCancelDisabled || syncingWithServer}
              data-test="terminal-payment-modal-change-terminal-button"
              onClick={handleTerminalSelectionOption}
            >
              Change terminal
            </Button>
          )}
          <Button
            variant="danger"
            fill
            ml={multiTerminal ? 2 : 0}
            data-test="cancel-btn"
            disabled={isCancelDisabled || syncingWithServer}
            onClick={handleCancelWhileInProgress}
          >
            Cancel payment
          </Button>
        </div>
      );
    case TransactionState.CONNECTION_DEGRADED:
    case TransactionState.CANCEL_FROM_POS_UNREFERENCED_REFUND:
      return (
        <div className="quick-pay-modal__footer quick-pay-modal__footer">
          <Button
            variant="danger"
            fill
            data-test="cancel-btn"
            disabled={isCancelDisabled || syncingWithServer}
            onClick={handleCancelWhileInProgress}
          >
            Cancel payment
          </Button>
        </div>
      );
    case TransactionState.CANCELED:
    case TransactionState.CANCELLED_FROM_POS:
    case TransactionState.CONNECTION_KO:
    case TransactionState.DECLINED:
    case TransactionState.ERROR:
    case TransactionState.FAILED:
    case TransactionState.TERMINAL_ERROR:
      return (
        <div className="quick-pay-modal__footer quick-pay-modal__footer">
          <Button data-test="terminal-payment-modal-close-button" onClick={handleCloseModal}>
            Close
          </Button>

          {showRetryButton && (
            <Button
              ml={2}
              variant="secondary"
              data-test="terminal-payment-modal-retry-button"
              fill
              onClick={handleRetryPaymentClicked}
            >
              Retry
            </Button>
          )}
        </div>
      );
    case TransactionState.CANCEL_UNAVAILABLE:
      return (
        <div className="quick-pay-modal__footer quick-pay-modal__footer">
          <Button onClick={handleBackInProgress} data-test="ok-btn">
            OK
          </Button>
        </div>
      );
    case TransactionState.SERVICE_EXCEPTION:
    case TransactionState.NON_CANCELLABLE_STATE:
      return (
        <div className="quick-pay-modal__footer quick-pay-modal__footer">
          <Button onClick={handleCloseModal}>Close</Button>
        </div>
      );
    case TransactionState.CANCEL_FROM_TERMINAL:
      return (
        <div className="quick-pay-modal__footer quick-pay-modal__footer">
          <Button onClick={handleBackInProgress} data-test="ok-btn">
            OK
          </Button>
        </div>
      );
    default:
      break;
  }

  return null;
};
